/**
 * Shared presentational atoms for the Signal Alpha Lab.
 */
import React from "react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Progress } from "../ui/progress";
import { RefreshCw, Play } from "lucide-react";

export const HORIZON_LABELS = { 21: "1M", 63: "3M", 126: "6M", 252: "12M" };
export const BENCH_LABELS = { spy: "vs S&P 500", sector: "vs Sector" };

/** Significance stars from a t-statistic. */
export const SigBadge = ({ t }) => {
  if (t === null || t === undefined)
    return <Badge variant="outline" className="text-xs">n/a</Badge>;
  const a = Math.abs(t);
  if (a >= 2.58)
    return <Badge className="bg-emerald-600 hover:bg-emerald-600 text-xs" data-testid="sig-badge-1pct">*** 1%</Badge>;
  if (a >= 1.96)
    return <Badge className="bg-green-600 hover:bg-green-600 text-xs" data-testid="sig-badge-5pct">** 5%</Badge>;
  if (a >= 1.65)
    return <Badge className="bg-yellow-600 hover:bg-yellow-600 text-xs" data-testid="sig-badge-10pct">* 10%</Badge>;
  return <Badge variant="secondary" className="text-xs" data-testid="sig-badge-ns">n.s.</Badge>;
};

export const AlphaVal = ({ value, digits = 2, suffix = "%" }) => {
  if (value === null || value === undefined)
    return <span className="text-muted-foreground">-</span>;
  const cls = Math.abs(value) < 0.005 ? "text-muted-foreground"
    : value > 0 ? "text-green-500" : "text-red-500";
  return (
    <span className={`font-mono ${cls}`}>
      {value > 0 ? "+" : ""}{value.toFixed(digits)}{suffix}
    </span>
  );
};

export const StatCard = ({ label, children, testId }) => (
  <div className="bg-muted/40 rounded-lg p-3" data-testid={testId}>
    <div className="text-xs text-muted-foreground mb-1">{label}</div>
    <div className="text-lg font-semibold">{children}</div>
  </div>
);

/** Pill-style toggle group. */
export const ToggleGroup = ({ options, value, onChange, testId }) => (
  <div className="inline-flex rounded-lg border p-0.5 gap-0.5" data-testid={testId}>
    {options.map((opt) => (
      <button
        key={opt.value}
        onClick={() => onChange(opt.value)}
        data-testid={`${testId}-${opt.value}`}
        className={`px-3 py-1 text-xs rounded-md transition-colors ${
          value === opt.value
            ? "bg-primary text-primary-foreground"
            : "text-muted-foreground hover:bg-muted"
        }`}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

/** Run/recompute bar with progress + computed-at metadata. */
export const RunBar = ({ label, result, job, running, onRun, testId }) => (
  <div className="flex flex-col gap-2" data-testid={testId}>
    <div className="flex items-center justify-between gap-4 flex-wrap">
      <div className="text-sm text-muted-foreground">
        {result ? (
          <>
            Computed{" "}
            <span className="font-medium text-foreground">
              {new Date(result.computed_at).toLocaleString()}
            </span>
            {result.duration_seconds != null && (
              <span className="ml-2">({Math.round(result.duration_seconds)}s)</span>
            )}
          </>
        ) : running ? (
          "Computing..."
        ) : (
          "Not computed yet — click Run Analysis"
        )}
      </div>
      <Button size="sm" onClick={onRun} disabled={running} data-testid={`${testId}-run-btn`}>
        {running ? (
          <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Running...</>
        ) : (
          <><Play className="w-4 h-4 mr-2" />{result ? "Recompute" : "Run Analysis"}</>
        )}
      </Button>
    </div>
    {running && job && (
      <div className="space-y-1">
        <Progress value={job.progress || 0} className="h-2" />
        <div className="text-xs text-muted-foreground">{job.message}</div>
      </div>
    )}
  </div>
);

export const PanelSummary = ({ panel, rebalance }) => {
  if (!panel) return null;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="panel-summary">
      <StatCard label="Observations">{panel.n_obs?.toLocaleString()}</StatCard>
      <StatCard label="Symbols">{panel.n_symbols?.toLocaleString()}</StatCard>
      <StatCard label="Rebalance Dates">{panel.n_dates}</StatCard>
      <StatCard label="Period">
        <span className="text-sm">
          {panel.date_range?.start} → {panel.date_range?.end}
        </span>
      </StatCard>
      {rebalance && (
        <div className="col-span-2 md:col-span-4 text-xs text-muted-foreground -mt-1">
          Rebalance: {rebalance}
        </div>
      )}
    </div>
  );
};
